import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger, NgxLoggerLevel } from 'ngx-logger';
import { Observable, of } from 'rxjs';

import { environment } from '../../../environments/environment';

type ServerLogPrimitive = string | number | boolean | null;
type ServerLogMeta = Record<string, ServerLogPrimitive>;

interface ServerLogErrorSummary {
    message: string;
    name?: string;
    status?: number;
    statusText?: string;
    url?: string;
}

@Injectable({ providedIn: 'root' })
export class LoggingService {
    private readonly maxStringLength = 4000;
    private readonly maxArrayLength = 20;
    private readonly maxContextItems = 5;
    private readonly allowedMetaKeys = new Set([
        'code',
        'method',
        'name',
        'responseType',
        'status',
        'statusCode',
        'statusText',
        'type',
        'url',
    ]);
    readonly serverLoggingUrl = environment.loggerConfig.serverLoggingUrl;

    constructor(
        private logger: NGXLogger,
        private http: HttpClient
    ) {}

    getLogging$(): Observable<{}> {
        return of({});
    }
    debug(message: string, ...additional: any[]) {
        this.logger.debug(message, ...this.sanitizeConsoleDetails(additional));
        this.sendToServer(NgxLoggerLevel.DEBUG, message, additional);
    }

    info(message: string, ...additional: any[]) {
        this.logger.info(message, ...this.sanitizeConsoleDetails(additional));
        this.sendToServer(NgxLoggerLevel.INFO, message, additional);
    }

    warn(message: string, ...additional: any[]) {
        this.logger.warn(message, ...this.sanitizeConsoleDetails(additional));
        this.sendToServer(NgxLoggerLevel.WARN, message, additional);
    }

    error(message: string, ...additional: any[]) {
        this.logger.error(message, ...this.sanitizeConsoleDetails(additional));
        this.sendToServer(NgxLoggerLevel.ERROR, message, additional);
    }

    // Checks if the logging level is set to DEBUG.
    isDebugEnabled(): boolean {
        return environment.loggerConfig.level === NgxLoggerLevel.DEBUG;
    }

    private sendToServer(level: NgxLoggerLevel, message: string, additional: any[]): void {
        if (!this.shouldSendToServer(level)) {
            return;
        }

        const details = this.extractServerDetails(additional);
        const payload: {
            level: NgxLoggerLevel;
            message: string;
            timestamp: string;
            context?: string[];
            meta?: ServerLogMeta;
            error?: ServerLogErrorSummary;
        } = {
            level,
            message: this.normalizeMessage(message),
            timestamp: new Date().toISOString(),
        };

        if (details.context.length > 0) {
            payload.context = details.context;
        }

        if (Object.keys(details.meta).length > 0) {
            payload.meta = details.meta;
        }

        if (details.error) {
            payload.error = details.error;
        }

        this.http.post(this.serverLoggingUrl, payload).subscribe({
            error: () => {
                // Avoid recursive log attempts when the logging endpoint itself fails.
            },
        });
    }

    private shouldSendToServer(level: NgxLoggerLevel): boolean {
        const serverLevel = environment.loggerConfig.serverLogLevel;
        return serverLevel !== NgxLoggerLevel.OFF && level >= serverLevel;
    }

    /**
     * Keep browser diagnostics subject to the same redaction rules as server
     * forwarding. This prevents tokens, full API responses, and arbitrary user
     * objects from being exposed through DevTools when INFO/DEBUG is enabled.
     */
    private sanitizeConsoleDetails(additional: unknown[]): unknown[] {
        const details = this.extractServerDetails(additional);
        const safe: unknown[] = [];

        if (details.error) {
            safe.push(details.error);
        }

        safe.push(...details.context);

        if (Object.keys(details.meta).length > 0) {
            safe.push(details.meta);
        }

        return safe;
    }

    private normalizeMessage(message: unknown): string {
        const normalized =
            typeof message === 'string'
                ? message
                : this.tryStringify(message, 'The provided "message" value could not be parsed with JSON.stringify().');

        return this.truncate(normalized);
    }

    private extractServerDetails(additional: unknown[]): {
        context: string[];
        meta: ServerLogMeta;
        error: ServerLogErrorSummary | null;
    } {
        const context: string[] = [];
        const meta: ServerLogMeta = {};
        let error: ServerLogErrorSummary | null = null;

        for (const item of additional.slice(0, this.maxArrayLength)) {
            if (!error) {
                error = this.summarizeError(item);
                if (error) {
                    continue;
                }
            }

            // String arguments are commonly filenames, display names, URLs, or
            // user input. Keep them out of logs; actionable text belongs in the
            // bounded message itself.
            if (typeof item === 'number' || typeof item === 'boolean') {
                if (context.length < this.maxContextItems) {
                    context.push(this.truncate(String(item)));
                }
                continue;
            }

            if (this.isPlainObject(item)) {
                Object.assign(meta, this.extractAllowedMeta(item));
            }
        }

        return { context, meta, error };
    }

    private summarizeError(value: unknown): ServerLogErrorSummary | null {
        if (value instanceof HttpErrorResponse) {
            return {
                message: this.truncate(value.message),
                name: value.name,
                status: value.status,
                statusText: this.truncate(value.statusText || ''),
                url: value.url ? this.truncate(value.url) : undefined,
            };
        }

        if (value instanceof Error) {
            return {
                message: this.truncate(value.message),
                name: value.name,
            };
        }

        return null;
    }

    private extractAllowedMeta(value: Record<string, unknown>): ServerLogMeta {
        const meta: ServerLogMeta = {};

        for (const [key, rawValue] of Object.entries(value)) {
            if (!this.allowedMetaKeys.has(key) || !this.isPrimitive(rawValue)) {
                continue;
            }

            meta[key] = typeof rawValue === 'string' ? this.truncate(rawValue) : rawValue;
        }

        return meta;
    }

    private isPrimitive(value: unknown): value is ServerLogPrimitive {
        return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null;
    }

    private isPlainObject(value: unknown): value is Record<string, unknown> {
        return !!value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Error);
    }

    private tryStringify(value: unknown, fallback: string): string {
        try {
            return JSON.stringify(value);
        } catch {
            return fallback;
        }
    }

    private truncate(value: string): string {
        if (value.length <= this.maxStringLength) {
            return value;
        }

        return `${value.slice(0, this.maxStringLength)}...[truncated]`;
    }
}
